/**
 * Search matching + data loading, with no DOM and no imports.
 *
 * Extracted from search.js so the relevance harness (scripts/search-eval/) can
 * import this file straight into the browser as a plain module and grade
 * exactly what the live site returns. If the two ever held their own copies of
 * the matching rules they would drift, and the scoreboard would be measuring
 * something the site doesn't do.
 */

export const MIN_QUERY = 2;

// Result ordering by type: looks first, then accessories, then everything else.
export const TYPE_RANK = { look: 0, accessory: 1 };
export const typeRank  = t => (TYPE_RANK[t] ?? 2);

const COLLECTIONS = ['items'];

// ── Matching ────────────────────────────────────────────────────────────────

const escapeRe = word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Family names expand to their shades at query time, so "red" finds a gown
// tagged burgundy and "blue" finds one tagged navy. One-directional on
// purpose: searching "burgundy" stays exact — it must not return every red in
// the archive. Membership follows the human relevance judgments in
// scripts/search-eval, not colour theory: navy and burgundy items were graded
// strong matches for blue/red, while olive, teal and sage items were graded
// wrong for green — so those stay out of the green family even though a colour
// wheel would put them nearby.
export const COLOUR_FAMILIES = {
    red:    ['burgundy', 'maroon', 'crimson', 'scarlet', 'coral'],
    blue:   ['navy', 'cobalt', 'indigo'],
    green:  ['emerald'],
    pink:   ['coral', 'blush', 'fuchsia', 'magenta', 'rose'],
    purple: ['violet', 'plum'],
    gray:   ['grey', 'charcoal'],
    grey:   ['gray', 'charcoal'],
    yellow: ['mustard'],
    orange: ['rust'],
    white:  ['ivory'],
};

const wordAlternation = word => {
    const shades = COLOUR_FAMILIES[word.toLowerCase()];
    return (shades ? [word, ...shades] : [word]).map(escapeRe).join('|');
};

// Word-prefix match: "silk" matches "silk", "silky" but NOT "silver"
export function matchesWordPrefix(text, word) {
    if (!text) return false;
    return new RegExp(`\\b(?:${wordAlternation(word)})`, 'i').test(text);
}

// All words in query must match somewhere across the item's fields
export function matchesItem(item, words) {
    const haystack = [item.title, item.keywords, item.type, item.collection_name].join(' ');
    return words.every(word => matchesWordPrefix(haystack, word));
}

// Words shorter than MIN_QUERY are dropped, so "a" and "of" never constrain.
export function queryWords(query) {
    return query.split(/\s+/).filter(w => w.length >= MIN_QUERY);
}

// ── Ranking ─────────────────────────────────────────────────────────────────
//
// Keywords are written by the AI tagger in a fixed order — colours first, then
// garments, materials, silhouette, footwear — and within the colour run they
// are ordered by prominence. Judging 120 colour results against the archive
// showed how sharp that signal is:
//
//     colour listed 1st  →  91% judged a strong match
//     colour listed 2nd  →  29%
//     colour listed 3rd  →  23%
//
// Search used to discard it entirely, which is why "gold" returned ten items
// that all contained gold and only two that WERE gold.
//
// The same test showed position means nothing outside the colour run (garment
// matches scored 1.92-2.00 regardless of where they sat) — there, position only
// reflects how many colours happened to be tagged first. So position is used
// as evidence only inside the colour block; every other match is neutral, which
// leaves already-good queries ("gown", "trouser", "fw13") in their current
// order rather than shuffling them on noise.

const COLOUR_WORDS = new Set([
    'beige', 'black', 'blue', 'blush', 'bronze', 'brown', 'burgundy', 'camel',
    'charcoal', 'cobalt', 'copper', 'coral', 'cream', 'crimson', 'emerald', 'fuchsia',
    'gold', 'gray', 'green', 'grey', 'indigo', 'ivory', 'khaki', 'lavender',
    'lilac', 'magenta', 'maroon', 'mauve', 'mint', 'mustard', 'navy', 'nude',
    'ochre', 'olive', 'orange', 'peach', 'pink', 'plum', 'purple', 'red',
    'rose', 'rust', 'sage', 'salmon', 'sand', 'scarlet', 'silver', 'skin',
    'tan', 'taupe', 'teal', 'turquoise', 'violet', 'white', 'yellow',
    // Finishes and non-hue terms the tagger writes in the same colour slots.
    'clear', 'colorful', 'colourful', 'iridescent', 'metallic', 'monochrome',
    'multicolor', 'multicolour', 'multicolored', 'multicoloured', 'neutral',
    'pastel', 'transparent',
]);

const COLOUR_MODIFIERS = new Set([
    'light', 'dark', 'bright', 'pale', 'deep', 'hot', 'soft', 'muted',
    'burnt', 'dusty', 'jet', 'off',
]);

/** "navy" and "dark green" are colours; "wrap dress" is not. */
export function isColourTerm(term) {
    const t = term.trim().toLowerCase();
    if (COLOUR_WORDS.has(t)) return true;
    const parts = t.split(/[\s-]+/);
    return parts.length === 2 && COLOUR_MODIFIERS.has(parts[0]) && COLOUR_WORDS.has(parts[1]);
}

/** How many leading terms are colours — i.e. where the colour run ends. */
function colourBlockLength(terms) {
    let n = 0;
    while (n < terms.length && isColourTerm(terms[n])) n++;
    return n;
}

// A neutral match sits between "second colour" and "third colour": better than
// a colour the tagger ranked third, worse than one it ranked second.
const NEUTRAL = 1.5;

// "lace-up sandals" answering a search for "lace" is the one real sense
// collision in this vocabulary. But most hyphenated terms — "long-sleeve",
// "wide-leg", "knee-high" — mean exactly what their leading word says, so this
// can't be a hard exclusion. A soft penalty settles it: where a query has both
// glued and standalone matches the standalone ones win, and where every match
// is glued they all shift equally and the order is untouched.
const GLUED_PENALTY = 2;

function gluedOnly(term, word) {
    const w = escapeRe(word);
    // Both spellings of the compound: "lace-up sandals" and "lace up heels".
    return new RegExp(`\\b${w}(?:-| up\\b)`, 'i').test(term)
        && !new RegExp(`\\b${w}\\b(?!-)(?! up\\b)`, 'i').test(term);
}

/** Lower is better. Best match site for one word across the item's fields. */
function wordScore(item, word) {
    const terms    = (item.keywords || '').split(',').map(s => s.trim()).filter(Boolean);
    const blockLen = colourBlockLength(terms);
    // Same family expansion as matching, so a gown tagged "burgundy" first
    // scores as a position-0 hit for the query "red".
    const re       = new RegExp(`\\b(?:${wordAlternation(word)})`, 'i');

    let best = Infinity;
    terms.forEach((term, i) => {
        if (!re.test(term)) return;
        const base = i < blockLen ? i : NEUTRAL;
        best = Math.min(best, base + (gluedOnly(term, word) ? GLUED_PENALTY : 0));
    });

    // Title, type and season match but carry no prominence — titles here are
    // mostly identifiers ("SS13 Look 12") and a season match applies to every
    // item in that collection equally.
    if (matchesWordPrefix([item.title, item.type, item.collection_name].join(' '), word)) {
        best = Math.min(best, NEUTRAL);
    }
    return best === Infinity ? NEUTRAL : best;
}

/**
 * Summed across words rather than taking the worst, so a colour still sorts a
 * compound query: in "red dress" the garment word is neutral for everything,
 * leaving the colour to break the tie.
 */
export function scoreItem(item, words) {
    return words.reduce((total, word) => total + wordScore(item, word), 0);
}

/**
 * Filter + order a dataset for a query. Returns [] when the query has no
 * usable words, so callers can tell "nothing matched" from "nothing to match".
 *
 * No result cap — the renderer paginates via infinite scroll, and the whole
 * catalogue is only ~700 records.
 */
export function runQuery(data, query) {
    const words = queryWords(query);
    if (!words.length) return [];

    return data
        .filter(item => matchesItem(item, words))
        .map(item => ({ item, score: scoreItem(item, words) }))
        // Match strength first, then the standing preference for looks over
        // accessories over everything else. Array.sort is stable, so equal
        // scores keep their existing order.
        .sort((a, b) => (a.score - b.score) || (typeRank(a.item.type) - typeRank(b.item.type)))
        .map(entry => entry.item);
}

// ── Data ────────────────────────────────────────────────────────────────────

async function fetchVideos(pbUrl) {
    const params = new URLSearchParams({
        perPage: 500,
        fields:  'id,collectionId,title,collection,thumbnail',
    });
    const res = await fetch(`${pbUrl}/api/collections/videos/records?${params}`);
    if (!res.ok) return [];
    const json = await res.json();
    return (json.items || []).map(v => ({
        ...v,
        type:     'video',
        keywords: ['video', v.collection, v.title].filter(Boolean).join(' '),
        file:     null,
    }));
}

async function fetchCollection(pbUrl, collection) {
    // Paginate — there are >500 items and PocketBase caps perPage at 500, so
    // a single page silently dropped the rest (which hid most looks from
    // search entirely). Loop until every page is fetched.
    const out = [];
    let page = 1, totalPages = 1;
    do {
        const params = new URLSearchParams({
            perPage: 500,
            page,
            fields:  'id,collectionId,title,type,keywords,file,img_blurhash,expand.collection.name',
            expand:  'collection',
        });
        const res = await fetch(`${pbUrl}/api/collections/${collection}/records?${params}`);
        if (!res.ok) throw new Error(`${collection}: HTTP ${res.status}`);
        const json = await res.json();
        totalPages = json.totalPages || 1;
        // Fold the collection name (e.g. "FW12") into a searchable field so a
        // season query returns every look in that collection, even when the
        // look's own title doesn't repeat the code.
        for (const { expand, ...it } of (json.items || [])) {
            out.push({ ...it, collection_name: expand?.collection?.name || '' });
        }
        page++;
    } while (page <= totalPages);
    return out;
}

/** Every searchable record: items (paginated) plus videos. */
export async function fetchAll(pbUrl) {
    const results = await Promise.all([
        ...COLLECTIONS.map(col => fetchCollection(pbUrl, col)),
        fetchVideos(pbUrl),
    ]);
    return results.flat();
}
